import {
  BarChart3,
  Bell,
  Mail,
  Megaphone,
  PlugZap,
  ClipboardCheck,
  Copy,
  CreditCard,
  FilePen,
  FileSpreadsheet,
  FileStack,
  FileText,
  FolderOpen,
  Gavel,
  Layers,
  LayoutDashboard,
  Palette,
  ScrollText,
  ShieldCheck,
  Trophy,
  Users,
  Wallet,
} from 'lucide-react'
import type { NavSection } from '@concours/ui'
import type { PermissionCode } from '@concours/shared'
import type { TFunction } from 'i18next'

/**
 * Menu piloté par configuration : chaque entrée déclare les permissions qui
 * la rendent visible (au moins une). Ajouter un module = ajouter une entrée.
 * `phase` sert à la page « en construction » tant que le module n'existe pas.
 */
export interface AdminNavItem {
  key: string
  href: string
  phase?: number
}

/** Convocations et documents officiels (phase 17). */
export const DOCUMENT_PERMISSIONS: PermissionCode[] = [
  'convocations.generate',
  'convocations.send',
  'centers.manage',
  'pv.generate',
  'results.view',
]

export function buildNavigation(t: TFunction): NavSection[] {
  return [
    {
      title: t('nav.pilotage'),
      items: [
        { label: t('nav.dashboard'), href: '/', icon: LayoutDashboard },
        { label: t('nav.competitions'), href: '/concours', icon: Trophy, anyOf: ['concours.view'] },
        { label: t('nav.forms'), href: '/formulaires', icon: FileText, anyOf: ['forms.view'] },
        {
          label: t('nav.exports'),
          href: '/exports',
          icon: FileSpreadsheet,
          anyOf: ['reports.export'],
        },
      ],
    },
    {
      title: t('nav.dossiers'),
      items: [
        {
          label: t('nav.applications'),
          href: '/candidatures',
          icon: FolderOpen,
          anyOf: ['candidature.view'],
        },
        {
          label: t('nav.corrections'),
          href: '/corrections',
          icon: FilePen,
          anyOf: ['corrections.view'],
        },
        { label: t('nav.payments'), href: '/paiements', icon: CreditCard, anyOf: ['payment.view'] },
        {
          label: t('nav.duplicates'),
          href: '/doublons',
          icon: Copy,
          anyOf: ['candidates.duplicates.review'],
        },
      ],
    },
    {
      title: t('nav.selection'),
      items: [
        { label: t('nav.stages'), href: '/selection', icon: Layers, anyOf: ['selection.view'] },
        {
          label: t('nav.commissions'),
          href: '/commissions',
          icon: Gavel,
          anyOf: ['notes.validate', 'evaluators.assign'],
        },
        { label: t('nav.grades'), href: '/notes', icon: ClipboardCheck, anyOf: ['notes.enter'] },
        { label: t('nav.results'), href: '/resultats', icon: BarChart3, anyOf: ['results.view'] },
        {
          label: t('nav.approvals'),
          href: '/resultats/approbations',
          icon: ShieldCheck,
          anyOf: ['results.approve', 'results.publish'],
        },
        {
          label: t('nav.documents'),
          href: '/documents',
          icon: FileStack,
          anyOf: DOCUMENT_PERMISSIONS,
        },
      ],
    },
    {
      title: t('nav.communication'),
      items: [
        {
          label: t('nav.campaigns'),
          href: '/notifications/envois',
          icon: Megaphone,
          anyOf: ['notifications.send'],
        },
        {
          label: t('nav.notificationHistory'),
          href: '/notifications/historique',
          icon: Bell,
          anyOf: ['notifications.send', 'notifications.templates.manage'],
        },
      ],
    },
    {
      title: t('nav.administration'),
      items: [
        { label: t('nav.users'), href: '/utilisateurs', icon: Users, anyOf: ['users.view'] },
        { label: t('nav.roles'), href: '/profils', icon: ShieldCheck, anyOf: ['roles.view'] },
        {
          label: t('nav.paymentMethods'),
          href: '/moyens-paiement',
          icon: Wallet,
          anyOf: ['payment_methods.view'],
        },
        {
          label: t('nav.notificationTemplates'),
          href: '/notifications/modeles',
          icon: Mail,
          anyOf: ['notifications.templates.manage'],
        },
        {
          label: t('nav.notificationProviders'),
          href: '/notifications/fournisseurs',
          icon: PlugZap,
          anyOf: ['notification_providers.manage'],
        },
        { label: t('nav.audit'), href: '/audit', icon: ScrollText, anyOf: ['audit.view'] },
        { label: t('nav.design'), href: '/design', icon: Palette },
      ],
    },
  ]
}

/** Modules pas encore livrés → phase de la feuille de route. */
export const PLANNED_MODULES: { href: string; navKey: string; phase: number }[] = []
